import { useState, type FormEvent } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { api, type ApiError } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'

// DIS-30: /join/{token} — register through an invite.
export const Route = createFileRoute('/join/$token')({
  loader: async ({ params }) => {
    const { data, error, response } = await api.GET('/invites/{token}', { params: { path: { token: params.token } } })
    if (data) return { invite: data, problem: null }
    return { invite: null, problem: (error as ApiError | undefined)?.message ?? `This invite can't be used (${response.status}).` }
  },
  component: JoinPage,
})

function JoinPage() {
  const { token } = Route.useParams()
  const { invite, problem } = Route.useLoaderData()
  const { auth } = Route.useRouteContext()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})

  if (!invite) {
    return (
      <Centered>
        <h1 className="text-lg font-semibold">Invite not available</h1>
        <p className="text-sm text-fg-muted">{problem}</p>
        <p className="text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-accent underline">
            Sign in
          </Link>
        </p>
      </Centered>
    )
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFields({})
    const form = new FormData(e.currentTarget)
    const body: { email?: string; display_name: string; password: string } = {
      display_name: String(form.get('display_name') ?? ''),
      password: String(form.get('password') ?? ''),
    }
    if (!invite!.email) body.email = String(form.get('email') ?? '')
    const { data, error: err, response } = await api.POST('/invites/{token}/accept', {
      params: { path: { token } },
      body,
    })
    setBusy(false)
    if (data) {
      await auth.invalidate()
      await navigate({ to: '/' })
      return
    }
    const apiErr = err as ApiError | undefined
    if (apiErr?.fields) setFields(apiErr.fields)
    setError(apiErr?.message ?? `Couldn't register (${response.status})`)
  }

  return (
    <Centered>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <h1 className="text-lg font-semibold">Join {invite.workspace_name}</h1>
          <p className="text-sm text-fg-muted">
            You've been invited as a <span className="font-medium">{invite.role}</span>. Create your account to get started.
          </p>
        </div>
        <Field label="Your name" error={fields.display_name}>
          <Input name="display_name" required maxLength={80} autoComplete="name" autoFocus />
        </Field>
        <Field label="Email" error={fields.email}>
          {invite.email ? (
            <Input name="email" type="email" value={invite.email} readOnly className="opacity-70" />
          ) : (
            <Input name="email" type="email" required autoComplete="username" />
          )}
        </Field>
        <Field label="Password" error={fields.password} hint="At least 12 characters.">
          <Input name="password" type="password" required minLength={12} autoComplete="new-password" />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md space-y-3 rounded-lg border border-border bg-bg-muted p-6">{children}</div>
    </div>
  )
}

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-fg-muted">{hint}</span>
      ) : null}
    </label>
  )
}
